import { normalizeGroundingSources } from './services/riskEventVetting.js';

const day = 86_400_000;
const unknown = /^(?:unknown|unverified|not verified|not applicable|n\/a|insufficient current intelligence|leadership status not verified)[.!]?$/i;
const notVerified = 'Leadership status not verified.';

export interface CountryResearch {
    asOf: string;
    candidate: any;
}

export function countryRequestCutoff(request: any): string | null {
    const text = (request?.contents || []).flatMap((content: any) => content.parts || [])
        .map((part: any) => part.text || '').join('\n');
    return [...text.matchAll(/CURRENT INFORMATION VERIFICATION — information cutoff: ([^\s]+)\./g)]
        .at(-1)?.[1] || null;
}

export async function researchCurrentCountry(request: any, generate: (request: any) => Promise<any>): Promise<CountryResearch | null> {
    const asOf = countryRequestCutoff(request);
    if (!asOf)
        return null;
    const text = (request.contents || []).flatMap((content: any) => content.parts || [])
        .map((part: any) => part.text || '').join('\n');
    const country = text.match(/"country"\s*:\s*"([^"\n]+)"/)?.[1];
    const response = await generate({ contents: [{ role: 'user', parts: [{ text:
        `Research current facts for ${country || `the country identified in this request: ${text.slice(0, 1500)}`} as of ${asOf} using live Google Search.
Prefer current official government records and corroborate with independent reliable reporting. Check publication and effective dates.
Return concise Markdown prose with inline citations, never JSON. At the start use separate labelled lines for Head of state,
Head of government, and Current status. Give the exact current full name and title, or explicitly state Unverified.
Then list the principal current officials with their exact names and current roles, followed by current political developments,
travel and health advisories, and significant events in the last 24 hours. Distinguish forecasts and annual data reference years.
Do not introduce former officials as incumbent leaders. Omit facts that cannot be verified and describe gaps.
This is factual research, not a scored risk assessment; no risk matrix or JSON schema is required.` }] }],
        tools: [{ googleSearch: {} }], generationConfig: { temperature: 0, maxOutputTokens: 8192 } })
        .catch(() => null);
    if (!response)
        return { asOf, candidate: null };
    return { asOf, candidate: (response.response || response).candidates?.[0] || null };
}

export function withCountryResearch(request: any, research: CountryResearch | null): any {
    if (!research?.candidate)
        return request;
    const text = (research.candidate.content?.parts || []).filter((part: any) => !part.thought)
        .map((part: any) => part.text || '').join('\n');
    return { ...request, contents: [...request.contents, { role: 'user', parts: [{ text:
        `FRESH SOURCE RESEARCH (checked for ${research.asOf}):\n${text}\nUse this research for all changing facts.
Copy verified leadership names, titles, and current status exactly; do not expand names into biographies.
Keep unsupported current facts unverified. Historical baseline summaries cannot override this current research.` }] }] };
}

export function isCurrentCountryWorkflow(workflow: string): boolean {
    return ['Generate Country Analysis', 'country_geopolitical_analysis',
        'Comprehensive Country Geopolitical & Risk Analysis', 'daily_micro_report',
        'daily_country_analysis'].includes(workflow);
}

export function currentCountryInformationRules(asOf: string): string {
    return `CURRENT INFORMATION VERIFICATION — information cutoff: ${asOf}.
Use Google Search during this request to verify facts that can change. Training knowledge, old academic
research, and saved country reports are historical context, never proof of current conditions.
Verify incumbent heads of state and government, officials' current roles, administrations, election outcomes,
active policies, travel and health advisories, and recent events against current authoritative sources.
Prefer official government and agency records, corroborated by independent reliable reporting.
Check each source's publication date, effective date, and whether its subject is incumbent or former.
A source retrieved today can describe an old administration; retrieval does not make historical facts current.
Apply verified corrections consistently throughout summaries, PMESII, government apparatus, leadership,
key figures, captions, and nested report copies. Identify historical officeholders explicitly as former.
Separate events in the last 24 hours from historical background and forecasts. Never report future events
as completed. For annual statistics, retain the latest available release and its reference year rather than
claiming that annual data describes today. Current official officeholder records need not be published today.
If a current fact cannot be verified, omit the assertion or state that it is unverified and record the gap.
Do not guess names, dates, advisory levels, sources, or citations. Resolve contradictory sources using their
effective dates and authority; leave unresolved assertions unverified rather than choosing from memory.
Use the fresh source research supplied with the request when available. Do not claim that every fact is verified.
Source gaps and unresolved conflicting assertions must be clearly identified, never concealed or guessed.
Return valid JSON without inline citation markup; retain the provider's grounding metadata.`;
}

function supportText(candidate: any, support: any, research = false): string {
    if (!research && typeof support?.segment?.text === 'string')
        return support.segment.text;
    const segment = support?.segment;
    const text = candidate?.content?.parts?.[segment?.partIndex || 0]?.text;
    if (typeof text !== 'string')
        return segment?.text || '';
    const bytes = new TextEncoder().encode(text);
    let start = segment?.startIndex || 0, end = segment?.endIndex;
    if (!Number.isInteger(end)) {
        const offset = typeof segment?.text === 'string' ? text.indexOf(segment.text) : -1;
        if (offset < 0) return segment?.text || '';
        start = new TextEncoder().encode(text.slice(0, offset)).length;
        end = start + new TextEncoder().encode(segment.text).length;
    }
    const exact = new TextDecoder().decode(bytes.slice(start, end));
    if (!research) return exact;
    const before = new TextDecoder().decode(bytes.slice(Math.max(0, start - 300), start)).split('\n\n').at(-1) || '';
    const after = new TextDecoder().decode(bytes.slice(end, end + 300)).split('\n\n')[0] || '';
    const context = before + exact + after;
    return /\bformer|\bprevious|\bpredecessor/i.test(context) ? exact : context;
}

export function verifyCurrentCountryReport(report: any, candidate: any, asOf: string, now = Date.now(), research?: CountryResearch | null): any {
    const evidence = research?.candidate || candidate;
    const informationAsOf = research?.asOf || asOf;
    const cutoff = Date.parse(informationAsOf);
    const fresh = Number.isFinite(cutoff) && cutoff <= now && now - cutoff <= day;
    const grounding = fresh ? evidence?.groundingMetadata : null;
    const chunks = grounding?.groundingChunks || [];
    const sources = normalizeGroundingSources(chunks);
    const gaps: string[] = [];
    if (!fresh) gaps.push('Current source research is unavailable or older than 24 hours.');
    if (!sources.length) gaps.push('The provider returned no citable current web sources. Current claims remain unverified.');
    else if (new Set(sources.map(source => source.publisher)).size < 2)
        gaps.push('Current research has only one identifiable publisher; independent corroboration is limited.');
    const normalize = (value: string) => value.toLowerCase().replace(/\./g, '').replace(/[\s\\]+/g, ' ').trim();
    const supported = (values: any[]) => values.every(value => typeof value === 'string' && value.trim())
        && (Array.isArray(grounding?.groundingSupports) ? grounding.groundingSupports : []).some((support: any) =>
            values.every(value => normalize(supportText(evidence, support, Boolean(research))).includes(normalize(value)))
            && normalizeGroundingSources((support.groundingChunkIndices || []).map((index: number) => chunks[index])).length > 0);
    let leadership = report?.leadershipOverview ? { ...report.leadershipOverview } : null;
    const rejectedNames: string[] = [];
    const researchText = (research?.candidate?.content?.parts || []).filter((part: any) => !part.thought)
        .map((part: any) => part.text || '').join('\n').replace(/\*\*/g, '');
    const researchLeader = (label: string) => {
        const value = researchText.match(new RegExp(`(?:^|\\n)\\s*${label}\\s*:\\s*([^\\n]+)`, 'i'))?.[1]
            ?.replace(/\[[^\]]*\](?:\([^)]*\))?|【[^】]*】/g, '').trim().replace(/\.$/, '');
        return value && !unknown.test(value) && supported([value]) ? value : null;
    };
    if (leadership) {
        for (const field of ['headOfState', 'headOfGovernment'])
            if (leadership[field] != null && !unknown.test(String(leadership[field]).trim()) && !supported([leadership[field]])) {
                const name = String(leadership[field]).split(',')[0].trim();
                const verified = researchLeader(field === 'headOfState' ? 'Head of state' : 'Head of government');
                if (!verified || !normalize(verified).includes(normalize(name))) rejectedNames.push(name);
                leadership[field] = verified || 'Unverified';
                if (leadership[field] === 'Unverified')
                    gaps.push(`Current ${field === 'headOfState' ? 'head of state' : 'head of government'} could not be independently verified.`);
            }
        if (leadership.currentStatus && !unknown.test(leadership.currentStatus) && !supported([leadership.currentStatus]))
            leadership.currentStatus = leadership.headOfState && !unknown.test(leadership.headOfState)
                ? `Current head of state: ${leadership.headOfState}` : notVerified;
        leadership.keyFigures = (Array.isArray(leadership.keyFigures) ? leadership.keyFigures : []).filter((figure: any) =>
            supported([figure?.name, figure?.role]));
        if (leadership.keyFigures.length < (report.leadershipOverview.keyFigures || []).length)
            gaps.push('Some current officials or their roles could not be independently verified.');
    }
    const { currentFactVerification: _verification, ...checked } = report;
    const scrub = (value: any): any => {
        if (typeof value === 'string')
            return value.split(/(?<=[.!?])\s+|\n/).map(sentence =>
                rejectedNames.some(name => name && sentence.toLowerCase().includes(name.toLowerCase()))
                && (!/\bformer|\bprevious|\bhistorical|\bleft office|\bserved until/i.test(sentence)
                    || /\bcurrent|\bcurrently|\bincumbent|\bremains|\bserves as/i.test(sentence))
                    ? 'Current official information in this passage could not be independently verified.' : sentence).join(' ');
        if (Array.isArray(value)) return value.map(scrub);
        if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype)
            return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrub(item)]));
        return value;
    };
    const originalGaps = checked.analystConfidence?.dataGaps;
    const safeReport = rejectedNames.length ? scrub(checked) : checked;
    const summary = safeReport.summary || safeReport.executiveSummary;
    return { ...safeReport, ...(summary ? { summary, executiveSummary: summary } : {}),
        ...(leadership ? { leadershipOverview: leadership } : {}), sources: chunks,
        ...(gaps.length ? { analystConfidence: { ...checked.analystConfidence,
            dataGaps: [...(Array.isArray(originalGaps) ? originalGaps : originalGaps ? [originalGaps] : []), ...gaps] } } : {}),
        currentInformation: {
        status: gaps.length ? 'limited' : 'source_checked', method: research ? 'grounded_research_then_json' : 'google_search_grounding',
        asOf: Number.isFinite(cutoff) ? informationAsOf : null, sources, gaps,
    } };
}
